/* eslint-disable class-methods-use-this */
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Project } from '../../domain/project/Project';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { VehicleDTO } from '../../domain/vehicle/VehicleDTO';
import { VehicleFlat } from '../../domain/vehicle/VehicleFlat';
import { VehicleFlatDTO } from '../../domain/vehicle/VehicleFlatDTO';
import { VehicleFlatMapper } from '../../mappers/vehicle/VehicleFlatMapper';
import { VehicleMapper } from '../../mappers/vehicle/VehicleMapper';
import { SessionRepository } from '../session/SessionRepository';
import { VehicleRepository } from './VehicleRepository';

export class VehicleRepositoryApi implements VehicleRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}
  async fetchVehicle({ vehicleId, project }: { vehicleId: string; project: Project }): Promise<Vehicle> {
    const response = await this.requests.get({
      uri: `/v2/vehicle/${vehicleId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as VehicleDTO;
    return VehicleMapper.toDomain(data, project.custom_fields?.vehicle ?? []);
  }
  async createVehicles({ vehicles, project }: { vehicles: Array<Vehicle>; project: Project }): Promise<Array<Vehicle>> {
    const response = await this.requests.post({
      uri: `/v2/vehicles?project_id=${project.id}`,
      data: vehicles.map(VehicleMapper.toRepository),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newVehicles = response.data as Array<VehicleDTO>;
    return newVehicles.map((newVehicle) => VehicleMapper.toDomain(newVehicle, project.custom_fields?.vehicle ?? []));
  }
  async save(vehicle: Vehicle, project: Project): Promise<Vehicle> {
    const response = await this.requests.put({
      uri: `/v2/vehicle/${vehicle.id}`,
      data: VehicleMapper.toRepository(vehicle),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return VehicleMapper.toDomain(response.data, project.custom_fields?.vehicle ?? []);
  }
  delete(vehicle: Vehicle): Promise<void> {
    throw new Error(`Method not implemented.`);
  }
  async search({
    project,
    offset,
    limit,
    searchText,
    sortBy,
    sortDirection,
    enabled,
  }: {
    project: Project;
    offset?: number;
    limit?: number;
    searchText?: string;
    sortBy?: string;
    sortDirection?: string;
    enabled?: boolean;
  }): Promise<PaginatedResult<Vehicle>> {
    const response = await this.requests.get({
      uri: `/v2/vehicles`,
      params: {
        project_id: project.id,
        ...(searchText ? { text: searchText } : {}),
        ...(sortBy ? { sort: sortBy } : {}),
        ...(sortBy && sortDirection ? { sort: `${sortBy}:${sortDirection}` } : {}),
        ...(offset ? { offset } : {}),
        ...(limit ? { limit } : {}),
        ...(enabled ? { enabled } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<VehicleDTO>;

    return {
      ...data,
      docs: data.docs.map((newVehicle) => VehicleMapper.toDomain(newVehicle, project.custom_fields?.vehicle ?? [])),
    };
  }

  async fetchFlatVehicles({ projectId }: { projectId: string }): Promise<Array<VehicleFlat>> {
    const response = await this.requests.get({
      uri: `/v2/vehicles/flat?project_id=${projectId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as Array<VehicleFlatDTO>;
    return data.map(VehicleFlatMapper.toDomain);
  }

  async createFromDrivers({
    driverIds,
    project,
  }: {
    driverIds: Array<string>;
    project: Project;
  }): Promise<Array<Vehicle>> {
    const response = await this.requests.post({
      uri: `/v2/vehicles/from_drivers?project_id=${project.id}`,
      data: { driver_ids: driverIds },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newVehicles = response.data as Array<VehicleDTO>;
    return newVehicles.map((newVehicle) => VehicleMapper.toDomain(newVehicle, project.custom_fields?.vehicle ?? []));
  }

  async updateConstraints(vehicleIds: string[], constraints: Record<string, any>): Promise<void> {
    await this.requests.put({
      uri: `/v2/vehicles/constraints`,
      data: {
        vehicle_ids: vehicleIds,
        constraints,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async deleteVehicles(vehicleIds: string[], project: Project): Promise<Vehicle[]> {
    const requests = vehicleIds.map((vehicleId) => {
      return {
        method: this.requests.delete.bind(this.requests),
        params: {
          uri: `/v2/vehicle/${vehicleId}`,
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const deletedVehicles = response.flat(1) as VehicleDTO[];

    return deletedVehicles
      .filter((vehicle) => vehicle !== null)
      .map((vehicle: VehicleDTO) => VehicleMapper.toDomain(vehicle, project.custom_fields?.vehicle ?? []));
  }

  async disableVehicles(vehicles: Vehicle[], project: Project): Promise<Vehicle[]> {
    const requests = vehicles.map((vehicle) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/vehicle/${vehicle.id}/enable`,
          data: {
            enabled: false,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedVehicles = response.flat(1) as VehicleDTO[];

    return updatedVehicles
      .filter((vehicle) => vehicle !== null)
      .map((vehicle: VehicleDTO) => VehicleMapper.toDomain(vehicle, project.custom_fields?.vehicle ?? []));
  }

  async enableVehicles(vehicles: Vehicle[], project: Project): Promise<Vehicle[]> {
    const requests = vehicles.map((vehicle) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/vehicle/${vehicle.id}/enable`,
          data: {
            enabled: true,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedVehicles = response.flat(1) as VehicleDTO[];

    return updatedVehicles
      .filter((vehicle) => vehicle !== null)
      .map((vehicle: VehicleDTO) => VehicleMapper.toDomain(vehicle, project.custom_fields?.vehicle ?? []));
  }

  async retrieveHasSession({ vehicleId }: { vehicleId: string }): Promise<boolean> {
    const response = await this.requests.get({
      uri: `/drivers/v3/vehicle/${vehicleId}/has-session`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as { has_session: boolean };

    return data.has_session;
  }
}
